<template>
  <div class="page-container expense-page">
    <portal to="page-name">Serviços, taxas e outras despesas</portal>
    <div class="loading loading-lg mt-2" v-if="loading"></div>
    <div class="card card-page" v-else>
      <div class="card-header">
        <div class="card-title">Gerenciamento de serviços - {{ getTitle }}</div>
        <div class="text-dark-gray mt-1">
          Configure regras, tipo e outros
        </div>
      </div>
      <div class="card-body">
        <st-tabs ref="expense-tabs" @changed="changeTabs">
          <st-tab id="tab-data" name="Dados principais">
            <div class="columns">
              <div class="column col-12 form-group">
                <label class="form-label">TUSS</label>
                <dx-autocomplete
                  id="tuss"
                  ref="tussAutocomplete"
                  v-model="tuss"
                  :readonly="!!form.tuss.code"
                  :source="findTuss"
                  :custom-label="setTussLabel"
                  track-by="id"
                  @select="selectTussItem"
                  :debounce="800"
                  placeholder="Nome ou código do TUSS"
                >
                  <template v-slot:action>
                    <button @click="unsetTuss"
                            class="btn btn-action input-group-btn btn-icon"
                            :class="tuss && tuss.code ? 'btn-gray' : 'btn-neutral'"
                            tabindex="-1">
                      <fa-icon
                        :icon="['fal', tuss && tuss.code ? 'times' : 'search']"
                      />
                    </button>
                  </template>
                  <template v-slot="{ item }">
                    <div>
                      {{ item.code | tuss }} - {{ item.name }} {{ item.description }}
                    </div>
                  </template>
                </dx-autocomplete>
              </div>
              <div class="column col-8 col-md-12 col-sm-12 form-group"
                   :class="{'has-error': $v.form.name.$error}">
                <label for="name" class="form-label">Nome</label>
                <input
                  type="text"
                  v-model="form.name" class="form-input"
                  @blur="$v.form.name.$touch()"
                  placeholder="Nome"
                  autocomplete="nope"
                >
              </div>
              <div class="column col-4 col-md-12 col-sm-12 form-group"
                   :class="{'has-error': $v.form.type.$error}">
                <label class="form-label">Tipo</label>
                <select
                  class="form-select"
                  v-model="form.type"
                  @blur="$v.form.type.$touch()"
                >
                  <option value="">[Selecione]</option>
                  <option v-for="(text, value) in types"
                          :value="value" :key="value">{{ text }}</option>
                </select>
              </div>
              <div class="column col-2 col-md-3 col-sm-12 form-group"
                   v-if="hasTimeOrValidity">
                <label class="form-label">Intervalo (min)</label>
                <dx-input-number
                  class="form-input text-center"
                  v-model="form.time"
                  maxlength="3"
                />
              </div>
              <div class="column col-2 col-md-3 col-sm-12 form-group"
                   v-if="hasTimeOrValidity">
                <label class="form-label">Vigência (dias)</label>
                <dx-input-number
                  class="form-input text-center"
                  v-model="form.validity"
                  maxlength="3"
                />
              </div>
              <div class="column col-8 col-md-6 col-sm-12 form-group"
                   :class="{'has-error': $v.form.specialtyCode.$error}"
                   v-if="hasSpecialty">
                <label class="form-label">Especialidade</label>
                <select class="form-select"
                        @blur="$v.form.specialtyCode.$touch()"
                        v-model="form.specialtyCode">
                  <option value="">[Selecione]</option>
                  <option v-for="(item, i) in specialties"
                          :value="item.code" :key="i">{{ item.name }}</option>
                </select>
              </div>
              <div class="column col-3 col-md-6 col-sm-12 form-group"
                   v-if="form.type === 'exam'">
                <label class="form-label">Modalidade</label>
                <select class="form-select"
                        v-model="form.modality">
                  <option value="">[Selecione]</option>
                  <option v-for="(text, value, i) in modalities"
                          :value="value" :key="i">
                    {{ value }} - {{ text }}
                  </option>
                </select>
              </div>
              <div class="column col-3 col-md-6 col-sm-12 form-group"
                   v-if="form.type === 'exam'">
                <label class="form-label">Entrega (dias)</label>
                <dx-input-number
                  class="form-input text-center"
                  v-model="form.medicalReportDays"
                  maxlength="3"
                  :disabled="form.type !== 'exam'"
                />
              </div>
              <div class="column col-2 col-md-6 col-sm-12 form-group"
                   v-if="form.type === 'exam'">
                <label class="form-label">Valor laudo</label>
                <dx-input-number
                  class="form-input"
                  v-model="form.medicalReportValue"
                  :precision="2"
                  maxlength="7"
                />
              </div>
              <div class="column col-3 col-md-12 col-sm-12 form-group"
                   v-if="hasProduct">
                <label class="form-label">Unidade de Entrada</label>
                <select class="form-select"
                        v-model="form.inputUnit">
                  <option value="">[Selecione]</option>
                  <option v-for="(text, value) in measures.measure"
                          :value="value" :key="value">{{ text }}</option>
                </select>
              </div>
              <div class="column col-3 col-md-12 col-sm-12 form-group"
                   v-if="hasProduct">
                <label class="form-label">Unidade de Saída</label>
                <select class="form-select"
                        v-model="form.outputUnit">
                  <option value="">[Selecione]</option>
                  <option v-for="(text, value) in measures.measure"
                          :value="value" :key="value">{{ text }}</option>
                </select>
              </div>
              <div class="column col-3 col-md-12 col-sm-12 form-group tooltip"
                   v-if="hasProduct"
                   :data-tooltip="`1 unidade de entrada representa ${
                     form.conversion} unidade(s) de saída`">
                <label class="form-label">Fator de conversão</label>
                <dx-input-number
                  class="form-input text-center"
                  v-model="form.conversion"
                />
              </div>
              <div class="column col-3 col-md-12 col-sm-12 form-group"
                   v-if="hasProduct">
                <label class="form-label">Custo</label>
                <dx-input-number
                  class="form-input text-right text-bold"
                  v-model="form.stock.cost"
                  :precision="2"
                  readonly
                />
              </div>
              <div class="column col-4 col-md-12 col-sm-12 form-group"
                   v-if="hasProduct">
                <label class="form-label">Grupo</label>
                <select class="form-select"
                        v-model="form.group.id">
                  <option value="">[Selecione]</option>
                  <option v-for="(item, i) in groups"
                          :value="item.id" :key="i">
                    {{ item.name }}
                  </option>
                </select>
              </div>
              <div class="column col-4 col-md-12 col-sm-12 form-group"
                   v-if="hasProduct">
                <label class="form-label">Família</label>
                <select class="form-select"
                        v-model="form.family.id">
                  <option value="">[Selecione]</option>
                  <option v-for="(item, i) in families"
                          :value="item.id" :key="i">
                    {{ item.name }}
                  </option>
                </select>
              </div>
              <div class="column col-2 col-md-12 col-sm-12 form-group"
                   v-if="hasProduct">
                <label class="form-label">Opme</label>
                <select class="form-select"
                        v-model="form.opme">
                  <option value="false">Não</option>
                  <option value="true">Sim</option>
                </select>
              </div>
              <div class="column col-2 col-md-12 col-sm-12 form-group"
                   v-if="hasProduct">
                <label class="form-label">Controla lote</label>
                <select class="form-select"
                        v-model="form.manageLot">
                  <option value="false">Não</option>
                  <option value="true">Sim</option>
                </select>
              </div>
              <div class="column col-4 col-md-12 col-sm-12 form-group"
                   v-if="hasProduct">
                <label class="form-label">Mínimo</label>
                <dx-input-number
                  class="form-input"
                  v-model="form.stock.min"
                  :precision="2"
                />
              </div>
              <div class="column col-4 col-md-12 col-sm-12 form-group"
                   v-if="hasProduct">
                <label class="form-label">Compra</label>
                <dx-input-number
                  class="form-input"
                  v-model="form.stock.buy"
                  :precision="2"
                />
              </div>
              <div class="column col-4 col-md-12 col-sm-12 form-group"
                   v-if="hasProduct">
                <label class="form-label">Máximo</label>
                <dx-input-number
                  class="form-input"
                  v-model="form.stock.max"
                  :precision="2"
                />
              </div>
              <div class="column col-12 form-group">
                <label class="form-label">Notas</label>
                <textarea
                  class="form-input"
                  rows="4"
                  v-model="form.notes"
                />
              </div>
            </div>
          </st-tab>
          <st-tab id="tab-aggregates" name="Agregados" v-if="!isNew">
            <label class="form-label">Pesquisar serviço</label>
            <dx-autocomplete
              v-model="aggregate"
              :source="findAggregate"
              label="name"
              track-by="id"
              :debounce="800"
              @select="addAggregate"
              placeholder="Informe o código ou nome do serviço..."
              input-id="expense-name">
              <template v-slot="{ item }">
                <a>
                  <fa-icon :icon="['fal', 'file-medical-alt']" class="text-primary"/>
                  {{ item.tuss?.code | tuss }} - {{ item.name }}
                </a>
              </template>
              <template v-slot:action>
                <button
                  type="button"
                  class="btn btn-action input-group-btn btn-icon btn-neutral"
                  @click="findExpenseModalShow = true"
                >
                  <fa-icon :icon="['fal', 'search']" />
                </button>
              </template>
            </dx-autocomplete>
            <div class="empty mt-2" v-if="form.aggregates.length === 0">
              <div class="empty-icon">
                <fa-icon :icon="['fal', 'info-circle']" size="2x"/>
              </div>
              <div class="empty-title h6">Agregados</div>
              <div class="empty-subtitle">Nenhum serviço adicionado</div>
            </div>
            <table class="table" v-else>
              <thead>
              <tr>
                <th>#</th>
                <th>Nome</th>
                <th class="text-center">Quantidade</th>
                <th></th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(item, i) in form.aggregates" :key="i">
                <td>{{ i + 1 }}</td>
                <td>
                  {{ item.name }} <small v-if="item.tuss">({{ item.tuss?.code | tuss }})</small>
                </td>
                <td class="text-center" style="width: 120px">
                  <dx-input-number
                    class="form-input input-sm text-center"
                    maxlength="4"
                    v-model="item.quantity"
                  />
                </td>
                <td class="text-right">
                  <button class="btn btn-icon btn-action btn-error btn-sm"
                          @click="removeAggregate(item.id)">
                    <fa-icon :icon="['fal', 'times']" />
                  </button>
                </td>
              </tr>
              </tbody>
            </table>
          </st-tab>
          <st-tab id="tab-preparation" name="Preparo" v-if="hasPreparation">
            <div class="columns">
              <div class="column col-12 form-group preparation">
                <label class="form-label">Informações de preparo</label>
                <st-editor id="preparation" v-model="form.preparation"></st-editor>
              </div>
            </div>
          </st-tab>
          <st-tab id="tab-tables" name="Tabelas de preços" v-if="!isNew">
            <expense-table
              v-if="form.id"
              origin="expense"
              :expense-id="form.id"
            />
          </st-tab>
          <st-tab id="tab-others" name="Outros" v-if="hasProduct">
            <div class="h6">Brasíndice</div>
            <div class="columns form-group">
              <div class="column col-5 col-md-12 col-sm-12 form-group">
                <label class="form-label">Nome</label>
                <dx-autocomplete
                  id="brasindice-name"
                  v-model="brasindice"
                  :readonly="!!form.brasindice.code"
                  :source="findBrasindice"
                  :custom-label="setBrasindiceName"
                  track-by="id"
                  @select="setBrasindice"
                  :debounce="800"
                  placeholder="Nome ou código do brasíndice"
                >
                  <template v-slot:action>
                    <button
                      class="btn btn-action input-group-btn btn-icon"
                      :class="brasindice ? 'btn-gray' : 'btn-neutral'"
                      @click="unsetBrasindice"
                      tabindex="-1"
                    >
                      <fa-icon :icon="['fal', brasindice ? 'times' : 'search']" />
                    </button>
                  </template>
                  <template v-slot="{ item }">
                    <div>
                      {{ item.name }}
                      {{ item.complement }}
                      <span v-if="item.factor">({{ item.factor }})</span>
                    </div>
                  </template>
                </dx-autocomplete>
              </div>
              <div class="column col-5 col-md-12 col-sm-12 form-group">
                <label class="form-label">Complemento</label>
                <input
                  type="text"
                  class="form-input"
                  v-model="form.brasindice.complement"
                  readonly>
              </div>
              <div class="column col-2 col-md-12 col-sm-12 form-group">
                <label class="form-label">Código</label>
                <input
                  type="text"
                  class="form-input text-center"
                  v-model="form.brasindice.code"
                  readonly>
              </div>
              <div class="column col-4 col-md-12 col-sm-12 form-group">
                <label class="form-label">Fabricante</label>
                <input
                  type="text"
                  class="form-input"
                  v-model="form.brasindice.manufacturer.name"
                  readonly>
              </div>
              <div class="column col-2 col-md-12 col-sm-12 form-group">
                <label class="form-label">Fator</label>
                <input
                  type="text"
                  class="form-input text-center"
                  v-model="form.brasindice.factor"
                  readonly>
              </div>
              <div class="column col-2 col-md-12 col-sm-12 form-group">
                <label class="form-label">Quantidade</label>
                <input
                  type="text"
                  class="form-input text-center"
                  v-model="form.brasindice.quantity"
                  readonly>
              </div>
              <div class="column col-2 col-md-12 col-sm-12 form-group">
                <label class="form-label">Valor Unitário</label>
                <dx-input-number
                  class="form-input"
                  v-model="form.brasindice.value"
                  :precision="2"
                  readonly
                />
              </div>
              <div class="column col-2 col-md-12 col-sm-12 form-group">
                <label class="form-label">Valor Total</label>
                <dx-input-number
                  class="form-input"
                  v-model="form.brasindice.total"
                  :precision="2"
                  readonly
                />
              </div>
            </div>
            <div class="divider" />
            <div class="columns form-group">
              <div class="column col-2 col-sm-12 form-group">
                <label class="form-label">Código NCM</label>
                <input
                  type="text"
                  class="form-input"
                  v-model="form.ncmCode"
                  placeholder="000000"
                  v-mask-number
                />
              </div>
              <div class="column col-2 col-sm-12 form-group">
                <label class="form-label">Código EAN</label>
                <input
                  type="text"
                  class="form-input"
                  v-model="form.eanCode"
                  placeholder="000000"
                  v-mask-number
                />
              </div>
              <div class="column col-2 col-sm-12 form-group">
                <label class="form-label">Registro Anvisa</label>
                <input
                  type="text"
                  class="form-input text-right"
                  v-model="form.anvisaRegistration"
                  placeholder="000000"
                />
              </div>
              <div class="column col-3 col-sm-12 form-group">
                <label class="form-label">Referência no fabricante</label>
                <input
                  type="text"
                  class="form-input text-right"
                  v-model="form.manufacturerReference"
                  placeholder="000000"
                />
              </div>
              <div class="column col-3 col-sm-12 form-group">
                <label class="form-label">AFE</label>
                <input
                  type="text"
                  class="form-input text-right tooltip"
                  data-tooltip="Autorização de funcionamento da empresa"
                  v-model="form.afe"
                  placeholder="000000"
                />
              </div>
              <div class="column col-2 col-sm-12 form-group">
                <label class="form-label">Portaria</label>
                <select class="form-select" v-model="form.regulation">
                  <option value="">Selecione</option>
                  <option value="344A1">344-A1</option>
                  <option value="344A2">344-A2</option>
                  <option value="344B1">344-B1</option>
                  <option value="344C1">344-C1</option>
                  <option value="344C2">344-C2</option>
                  <option value="344C5">344-C5</option>
                </select>
              </div>
              <div class="column col-2 col-sm-12 form-group">
                <label class="form-label">Código DCB</label>
                <div class="input-group">
                  <input type="text"
                         class="form-input text-right"
                         v-model="form.dcb.code"
                         @change="findDcb"
                         v-mask-number
                  />
                  <button class="btn btn-neutral btn-action input-group-btn btn-icon"
                          :class="{loading: dcbModal.loading}"
                          @click="openDcbModal">
                    <fa-icon :icon="['fal', 'search']" />
                  </button>
                </div>
              </div>
              <div class="column form-group">
                <label class="form-label">Descrição</label>
                <input
                  type="text"
                  class="form-input"
                  v-model="form.dcb.name"
                  readonly
                />
              </div>
            </div>
            <div class="divider" />
            <div class="columns form-group">
              <div class="column col-12 col-md-12 col-sm-12">
                <label class="form-label">Fornecedor(es)</label>
                <dx-autocomplete
                  id="provider"
                  ref="providerAutocomplete"
                  v-model="provider"
                  :source="findProvider"
                  :custom-label="setProviderLabel"
                  track-by="id"
                  @select="selectProviderItem"
                  :debounce="800"
                  placeholder="Informe o nome, código ou lote"
                >
                  <template v-slot:action>
                    <button class="btn btn-action input-group-btn btn-icon btn-neutral">
                      <fa-icon :icon="['fal', 'search']" />
                    </button>
                  </template>
                  <template v-slot="{ item }">
                    <div>{{ item.name }}</div>
                  </template>
                </dx-autocomplete>
              </div>
            </div>
            <div class="empty mt-2" v-if="form.providers.length === 0">
              <div class="empty-icon">
                <fa-icon :icon="['fal', 'info-circle']" size="2x" />
              </div>
              <p class="empty-title h6">Fornecedores</p>
              <p class="empty-subtitle">
                Nenhum fornecedor adicionado. Utilize o campo acima.
              </p>
            </div>
            <table class="table table-striped table-hover" v-else>
              <thead>
              <tr>
                <th class="hide-sm" style="width: 40px">#</th>
                <th>Nome</th>
                <th/>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(item, i) in form.providers" :key="i">
                <td class="hide-sm">{{ i + 1 }}</td>
                <td>{{ item.name }}</td>
                <td class="text-right">
                  <button class="btn btn-sm btn-action btn-icon btn-error"
                          @click="removeProviderItem(item.id)">
                    <fa-icon :icon="['fal', 'times']" />
                  </button>
                </td>
              </tr>
              </tbody>
            </table>
          </st-tab>
        </st-tabs>
      </div>
      <div class="card-footer">
        <button class="btn btn-primary mr-1"
                :class="{loading: saving}"
                :disabled="saving"
                @click="save"
                v-if="!hideSaveButton">
          Salvar
        </button>
        <button class="btn" @click="$router.back()">
          Voltar
        </button>
        <button class="btn btn-icon btn-icon-left btn-secondary float-right"
                v-if="form.id && $can(UPDATE_SCHEDULE)"
                @click="scheduleConfigModalShow = true">
          <fa-icon :icon="['fal', 'cog']" />
          Gerenciar serviços nas agendas
        </button>
      </div>
    </div>

    <dx-modal title="Pesquisar DCB" id="dcb-modal" v-model="dcbModal.show">
      <div class="columns">
        <div class="column form-group">
          <div class="input-group">
            <input
              type="text"
              class="form-input"
              v-model="dcbModal.search"
              placeholder="Informe o nome do produto..."
              v-focus>
            <button class="btn btn-neutral btn-action input-group-btn btn-icon"
                    :class="{loading: dcbModal.loading}" tabindex="-1">
              <fa-icon :icon="['fal', 'search']" />
            </button>
          </div>
        </div>
      </div>
      <div class="empty mt-2" v-if="dcbModal.items.length === 0">
        <div class="empty-icon">
          <fa-icon :icon="['fal', 'info-circle']" size="3x" />
        </div>
        <p class="empty-title h5">Lista de DCB`s</p>
        <p class="empty-subtitle">
          Nenhum registro encontrado.
        </p>
      </div>
      <table class="table table-hover table-striped" v-else>
        <thead>
        <tr>
          <th>Código</th>
          <th>Descrição</th>
        </tr>
        </thead>
        <tbody>
        <tr class="c-hand" v-for="(item, i) in dcbModal.items" :key="i"
            @click="setDcb(item)">
          <td>{{ item.code }}</td>
          <td>{{ item.name }}</td>
        </tr>
        </tbody>
      </table>
      <template v-slot:footer>
        <button class="btn" @click="dcbModal.show = false">Sair</button>
      </template>
    </dx-modal>

    <find-expense-modal
      v-if="findExpenseModalShow"
      @selected="findExpenseModalSelected"
      @close="findExpenseModalShow = false"
    />

    <schedule-config-modal
      v-if="form.id && scheduleConfigModalShow"
      type="expense"
      :id="form.id"
      :name="form.name"
      @close="scheduleConfigModalShow = false"
    />
  </div>
</template>

<script>
import { modalities } from '@/data/expense-modalities';
import { types } from 'src/data/expense-types';
import { required } from 'vuelidate/src/validators';
import { mergeFrom } from '@/helpers/object';
import * as measures from 'src/data/measure-units';
import FindExpenseModal from '@/app/expense/components/modals/FindExpenseModal.vue';
import { UPDATE_SCHEDULE } from '@/data/actions/modules/clinical';
import ExpenseTable from '../../expense-table/components/ExpenseTable.vue';
import ScheduleConfigModal from '../../schedule/components/modals/Config.vue';
import formMixin from '../../../mixins/form';
import tussFilter from '../../../filters/tuss';

export default {
  components: {
    ExpenseTable,
    FindExpenseModal,
    ScheduleConfigModal,
  },
  mixins: [formMixin],
  data() {
    return {
      UPDATE_SCHEDULE,
      isNew: false,
      loadedType: '',
      modalities,
      measures,
      loading: false,
      saving: false,
      hideSaveButton: false,
      debounceTimeout: null,
      tuss: null,
      types,
      brasindice: null,
      provider: null,
      aggregate: null,
      specialties: [],
      families: [],
      groups: [],
      dcbModal: {
        loading: false,
        show: false,
        search: '',
        items: [],
      },
      findExpenseModalShow: false,
      scheduleConfigModalShow: false,
      form: this.blankForm(),
    };
  },
  watch: {
    'dcbModal.search': {
      handler() {
        clearTimeout(this.debounceTimeout);
        this.debounceTimeout = setTimeout(this.loadDcb, 600);
      },
      deep: true,
    },
  },
  computed: {
    hasTimeOrValidity() {
      return [
        'consultation',
        'session',
        'surgery',
        'exam',
        'procedure',
        'evaluation',
      ].includes(this.form.type);
    },
    hasSpecialty() {
      return [
        'consultation',
        'session',
        'evaluation',
      ].includes(this.form.type);
    },
    hasProduct() {
      return [
        'medicine',
        'material',
      ].includes(this.form.type);
    },
    hasPreparation() {
      return [
        'surgery',
        'exam',
        'procedure',
      ].includes(this.form.type);
    },
    getTitle() {
      if (!this.form.id) {
        return 'Novo Registro';
      }
      return this.form.name;
    },
  },
  validations() {
    const rules = {
      form: {
        name: { required },
        type: { required },
        specialtyCode: {},
      },
    };

    if (this.hasSpecialty) {
      rules.form.specialtyCode = { required };
    }

    return rules;
  },
  async mounted() {
    this.isNew = /create$/.test(this.$route.path);
    this.loadSpecialties();
    this.loadGroups();
    this.loadFamilies();
    if (!this.isNew) {
      this.form.id = this.$route.params.id;
      this.load();
    }
  },
  methods: {
    load() {
      this.loading = true;

      return this.$http
        .get(`/expenses/${this.form.id}`)
        .then(({ data }) => {
          this.form = mergeFrom(this.blankForm(), data);
          this.loadedType = data.type;

          if (data.specialty && data.specialty.code) {
            this.form.specialtyCode = data.specialty.code;
          }

          if (data.tuss && data.tuss.code) {
            this.tuss = data.tuss;
          }

          if (data.brasindice && data.brasindice.code) {
            this.brasindice = {
              code: data.brasindice.code,
              name: data.brasindice.name,
              complement: data.brasindice.complement,
              factor: data.brasindice.factor,
            };
          }
        })
        .catch((e) => {
          if (e.response && e.response.status === 404) {
            this.$toast.error('Serviço não encontrado nesta unidade!');
            this.$router.replace('/clinical/expenses');
            return;
          }
          this.$toast.error('Ocorreu um erro ao carregar o registro!');
        })
        .finally(() => {
          this.loading = false;
        });
    },
    loadSpecialties() {
      const params = {
        limit: 0,
        isSpecialty: true,
      };

      return this.$http.get('/services/cbos', { params })
        .then(({ data }) => {
          this.specialties = data.items;
        })
        .catch(() => {
          this.$toast.error('Ocorreu um erro ao carregar as especialidades!');
        });
    },
    async save() {
      if (this.saving) {
        return;
      }

      this.$v.form.$touch();
      if (this.$v.form.$error) {
        this.$refs['expense-tabs'].selectTab('tab-data');
        return;
      }

      this.saving = true;

      const params = {
        name: this.form.name,
        active: true,
      };

      try {
        const { data: found } = await this.$http.get('/expenses', { params });

        if (found.items.some(({ id }) => id !== this.form.id)) {
          this.$toast.show('Já existe um registro com este nome!', { type: 'error' });
          this.saving = false;
          return;
        }
      } catch (e) {
        this.$toast.error('Ocorreu um erro ao salvar o registro!');
        this.saving = false;
        return;
      }

      if (this.isNew || this.form.type !== this.loadedType) {
        if (!this.hasSpecialty) {
          this.form.specialtyCode = '';
        }
        if (this.form.type !== 'exam') {
          this.form.modality = '';
          this.form.medicalReportDays = '';
          this.form.medicalReportValue = '';
        }
        if (!this.hasPreparation) {
          this.form.preparation = '';
        }
        if (!this.hasProduct) {
          this.form.family = { id: '', name: '' };
          this.form.group = { id: '', name: '' };
        }
      }

      const service = this.clone(this.form);

      service.familyId = service.family.id;
      service.groupId = service.group.id;
      delete service.family;
      delete service.group;

      if (!service.dcb.code) {
        service.dcb = null;
      }

      if (!service.tuss.code) {
        service.tuss = null;
      }

      if (!service.brasindice.code) {
        service.brasindice = null;
      }

      const request = this.isNew
        ? this.$http.post('/expenses', service)
        : this.$http.put(`/expenses/${this.form.id}`, service);

      // eslint-disable-next-line consistent-return
      return request
        .then(({ data }) => {
          if (this.isNew) {
            this.$router.replace(`/clinical/expenses/${data.id}/edit`);
            this.isNew = false;
            this.form.id = data.id;
          }
          this.loadedType = this.form.type;
          this.$toast.show('Registro salvo');
        })
        .catch(() => {
          this.$toast.error('Ocorreu um erro ao salvar o registro!');
        })
        .finally(() => {
          this.saving = false;
        });
    },
    changeTabs({ tab }) {
      this.hideSaveButton = tab.hash === 'tab-insurances';
    },
    setTussLabel(item) {
      return item ? `${tussFilter(item.code)} - ${item.name}` : '';
    },
    selectTussItem(item) {
      this.form.tuss.code = item.code;
      this.form.tuss.name = item.name;
      if (!this.form.name && item.name) {
        this.form.name = item.name;
      }
    },
    findTuss(text) {
      const value = text.trim();

      const params = {
        search: value,
        types: 'material,medicine,service,procedure',
      };

      return this.$http.get('/tuss', { params })
        .then(({ data }) => data.items)
        .catch(() => {});
    },
    unsetTuss() {
      this.$refs.tussAutocomplete.searchText = null;
      this.form.tuss = {
        code: '',
        name: '',
      };
      this.tuss = null;
    },
    findDcb() {
      if (!this.form.dcb.code) {
        this.form.dcb.name = '';
        return null;
      }
      if (this.form.dcb.code.length < 5) {
        this.$toast.show('Código inválido!', { type: 'error' });
        this.form.dcb.code = '';
        this.form.dcb.name = '';
        return null;
      }

      this.dcbModal.loading = true;

      return this.$http
        .get(`/dcbs/${this.form.dcb.code}`)
        .then(({ data }) => {
          if (data) {
            this.form.dcb = data;
          } else {
            this.$toast.show('DCB não encontrado!', { type: 'error' });
            this.form.dcb.code = '';
            this.form.dcb.name = '';
          }
        })
        .catch(() => {})
        .finally(() => {
          this.dcbModal.loading = false;
        });
    },
    openDcbModal() {
      this.dcbModal.search = '';
      this.dcbModal.items = [];
      this.dcbModal.show = true;
    },
    loadDcb() {
      if (!this.dcbModal.search) {
        return null;
      }

      this.dcbModal.loading = true;

      const params = {
        search: this.dcbModal.search,
      };

      return this.$http
        .get('/dcbs/', { params })
        .then(({ data }) => {
          this.dcbModal.items = data.items;
        })
        .catch(() => {})
        .finally(() => {
          this.dcbModal.loading = false;
        });
    },
    setDcb(item) {
      this.form.dcb = item;
      this.dcbModal.show = false;
    },
    findBrasindice(search) {
      const params = {
        search,
      };

      return this.$http.get('/brasindice', { params })
        .then(({ data }) => {
          if (data.items.length === 0) {
            return [{ id: null, name: search }];
          }
          return data.items;
        })
        .catch(() => {});
    },
    setBrasindice(data) {
      this.form.brasindice.code = data ? data.code : null;
      this.form.brasindice.name = data ? data.name : null;
      this.form.brasindice.complement = data ? data.complement : null;
      this.form.brasindice.tussCode = data ? data.tussCode : null;
      this.form.brasindice.manufacturer = data ? data.manufacturer : {};
      this.form.brasindice.factor = data ? data.factor : null;
      this.form.brasindice.quantity = data ? data.quantity : null;
      this.form.brasindice.value = data ? data.value : null;
      this.form.brasindice.total = data ? data.total : null;
    },
    unsetBrasindice() {
      this.setBrasindice(null);
      this.brasindice = null;
    },
    setBrasindiceName(item) {
      return `${item.name} ${item.complement} (${item.factor})`;
    },
    loadGroups() {
      const params = {
        limit: 0,
        active: true,
      };

      return this.$http.get('/product-groups', { params })
        .then(({ data }) => {
          this.groups = data.items;
        })
        .catch(() => {});
    },
    loadFamilies() {
      const params = {
        limit: 0,
        active: true,
      };

      return this.$http.get('/product-families', { params })
        .then(({ data }) => {
          this.families = data.items;
        })
        .catch(() => {});
    },
    findProvider(text) {
      const value = text.trim();

      const params = {
        search: value,
        limit: 10,
      };

      return this.$http.get('/providers', { params })
        .then(({ data }) => data.items)
        .catch(() => {});
    },
    setProviderLabel(item) {
      return item.name || null;
    },
    selectProviderItem(item) {
      const found = this.form.providers.find(({ id }) => id === item.id);

      if (!found) {
        this.form.providers.push({
          id: item.id,
          name: item.name,
          email: item.email,
        });
      }

      this.provider = null;
    },
    removeProviderItem(id) {
      const idx = this.form.providers.findIndex(({ id: item }) => item === id);
      this.form.providers.splice(idx, 1);
    },
    findAggregate(search) {
      const params = {
        search,
        limit: 30,
        offset: 0,
        active: true,
      };

      return this.$httpX
        .get('/expenses', { params })
        .then(({ data }) => {
          if (data.items.length === 0) {
            return [{
              id: null,
              name: 'Serviço não encontrado!',
              quantity: 0,
            }];
          }
          return data.items;
        });
    },
    addAggregate(expense) {
      if (!expense || !expense.id) {
        this.aggregate = null;
        return;
      }

      if (expense.id === this.form.id) {
        this.$toast.error('O serviço não pode ser agregado dele mesmo!');
        this.aggregate = null;
        return;
      }

      const found = this.form.aggregates
        .find(({ id }) => id === expense.id);

      if (found) {
        found.quantity += 1;
      } else {
        this.form.aggregates.push({
          id: expense.id,
          name: expense.name,
          type: expense.type,
          modality: expense.modality,
          tuss: expense.tuss,
          quantity: 1,
        });
      }

      this.aggregate = null;
    },
    removeAggregate(id) {
      const idx = this.form.aggregates.findIndex(item => item.id === id);
      this.form.aggregates.splice(idx, 1);
    },
    findExpenseModalSelected(items) {
      if (!items) {
        return;
      }

      this.aggregate = null;

      if (items.some(({ id }) => id === this.form.id)) {
        this.$toast.error('O serviço não pode ser agregado dele mesmo!');
      }

      items.filter(({ id }) => id !== this.form.id).forEach((item) => {
        const found = this.form.aggregates
          .find(({ id }) => id === item.id);

        if (found) {
          found.quantity += 1;
        } else {
          this.form.aggregates.push({
            id: item.id,
            type: item.type,
            name: item.name,
            modality: item.modality,
            tuss: item?.tuss || null,
            quantity: 1,
          });
        }
      });

      this.findExpenseModalShow = false;
    },
    blankForm() {
      return {
        id: '',
        name: '',
        complement: '',
        type: '',
        modality: '',
        time: '',
        tuss: {
          code: '',
          name: '',
        },
        opme: false,
        manageLot: false,
        inputUnit: '',
        outputUnit: '',
        conversion: '1',
        group: {
          id: '',
          name: '',
        },
        family: {
          id: '',
          name: '',
        },
        preparation: '',
        medicalReportDays: '',
        medicalReportValue: '',
        validity: '',
        specialtyCode: '',
        specialty: {
          code: '',
          name: '',
        },
        stock: {
          min: '',
          buy: '',
          max: '',
          cost: '',
        },
        brasindice: {
          code: '',
          name: '',
          complement: '',
          tussCode: '',
          manufacturer: {
            code: '',
            name: '',
          },
          factor: '',
          quantity: '',
          value: '',
          total: '',
        },
        ncmCode: '',
        eanCode: '',
        regulation: '',
        anvisaRegistration: '',
        manufacturerReference: '',
        afe: '',
        dcb: {
          code: '',
          name: '',
        },
        providers: [],
        aggregates: [],
        notes: '',
        active: true,
      };
    },
  },
};
</script>

<style lang="scss">
@import '@/assets/scss/_variables.scss';
.expense-page {
  .preparation {
    .document-editor {
      .document-editor-body {
        .ck.ck-content.ck-editor__editable {
          min-height: 20rem;
        }
      }
    }
  }
}
</style>
